import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, type Ref, unref } from "vue";
import type {
	CalendarSpec,
	EntityDefinition,
	FieldDefinition,
	FieldType,
	OrderSpec,
	PublicViewConfig,
	RelationDefinition,
	ViewDefinition,
	ViewKind,
} from "@/shared/contracts/structure";
import { request } from "@/shared/http/client";

export const structureKeys = { entities: () => ["structure", "entities"] as const };

export function useStructure(enabled: () => boolean = () => true) {
	return useQuery({
		enabled,
		queryKey: structureKeys.entities(),
		queryFn: async (): Promise<EntityDefinition[]> => {
			const res = await request<{ entities: EntityDefinition[] }>("/v3/structure/entities");
			return res.entities;
		},
		// Structure changes when the builder changes it, which is rare. A long stale
		// time keeps every navigation render from refetching.
		staleTime: 60_000,
	});
}

export function useArchivedStructure(enabled: () => boolean) {
	return useQuery({
		enabled,
		queryKey: ["structure", "archived"],
		queryFn: async () =>
			(await request<{ entities: EntityDefinition[] }>("/v3/structure/archived-entities")).entities,
	});
}

export function useRestoreEntity() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (code: string) =>
			request<{ restored: boolean }>(`/v3/structure/entities/${code}/restore`, { method: "POST" }),
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ["structure"] });
			void client.invalidateQueries({ queryKey: ["system"] });
			void client.invalidateQueries({ queryKey: ["board"] });
		},
	});
}

export const viewKeys = {
	views: (entity: string) => ["structure", "views", entity] as const,
	relations: (entity: string) => ["structure", "relations", entity] as const,
};

/** One table's views — `useViews` and the home's agenda (one per dated table) read it. */
export async function fetchViews(code: string): Promise<ViewDefinition[]> {
	return (await request<{ views: ViewDefinition[] }>(`/v3/structure/entities/${code}/views`)).views;
}

export function useViews(entity: Ref<string> | string) {
	const code = computed(() => unref(entity));
	return useQuery({
		queryKey: computed(() => viewKeys.views(code.value)),
		queryFn: () => fetchViews(code.value),
		enabled: computed(() => code.value.length > 0),
		staleTime: 30_000,
	});
}

export function useRelations(entity: Ref<string> | string) {
	const code = computed(() => unref(entity));
	return useQuery({
		queryKey: computed(() => viewKeys.relations(code.value)),
		queryFn: async (): Promise<RelationDefinition[]> => {
			const res = await request<{ relations: RelationDefinition[] }>(
				`/v3/structure/entities/${code.value}/relations`,
			);
			return res.relations;
		},
		enabled: computed(() => code.value.length > 0),
		staleTime: 30_000,
	});
}

/**
 * Removing ONE view of an entity — a form design, a list, a component screen.
 *
 * The server does not refuse the last view: an entity with none falls back to
 * one column by field order, which is how it drew before views existed. So the
 * screen asks once and does not add a rule the server does not have.
 */
export function useDeleteView(entity: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: { viewCode: string }) =>
			request<{ deleted: boolean }>(
				`/v3/structure/entities/${unref(entity)}/views/${input.viewCode}`,
				{ method: "DELETE" },
			),
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: viewKeys.views(unref(entity)) });
			// A component view is a menu item (`/v3/structure/component-screens`, read
			// by the navigation under `["structure", "component", "screens"]`): the
			// prefix reaches it without this feature importing that one.
			void client.invalidateQueries({ queryKey: ["structure", "component"] });
		},
	});
}

export function useSaveView(entity: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			code: string;
			name: string;
			kind: ViewKind;
			isDefault?: boolean;
			fieldCodes?: readonly string[];
			calendar?: CalendarSpec;
			/**
			 * A ordem e o agrupamento que a view guarda.
			 *
			 * O servidor grava `orderSpec` como jsonb desde que views existem
			 * (`updateViewSchema` o aceita livre) e NADA no front o escrevia — uma view
			 * podia dizer "por etapa, mais recentes primeiro" e não havia como salvar isso.
			 * Vai no PATCH junto com o resto porque é uma propriedade da view, não uma
			 * segunda rota.
			 */
			orderSpec?: OrderSpec;
			/** True when the view already exists — PATCH rather than POST. */
			exists?: boolean;
		}) => {
			const base = `/v3/structure/entities/${unref(entity)}/views`;
			return input.exists
				? request<ViewDefinition>(`${base}/${input.code}`, {
						method: "PATCH",
						body: {
							name: input.name,
							fieldCodes: input.fieldCodes,
							calendar: input.calendar,
							isDefault: input.isDefault,
							// Ausente não apaga: o servidor só grava o que veio no corpo, então
							// salvar o nome de uma view não pode zerar a ordem dela.
							...(input.orderSpec ? { orderSpec: input.orderSpec } : {}),
						},
					})
				: request<ViewDefinition>(base, {
						method: "POST",
						body: {
							code: input.code,
							name: input.name,
							kind: input.kind,
							isDefault: input.isDefault,
							fieldCodes: input.fieldCodes,
							calendar: input.calendar,
						},
					});
		},
		onSuccess: () => client.invalidateQueries({ queryKey: viewKeys.views(unref(entity)) }),
	});
}

export function usePublishView(entity: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			viewCode: string;
			isPublic: boolean;
			publicSlug?: string | null;
			publicConfig?: PublicViewConfig;
		}) =>
			request<ViewDefinition>(
				`/v3/structure/entities/${unref(entity)}/views/${input.viewCode}/publish`,
				{
					method: "POST",
					body: {
						isPublic: input.isPublic,
						publicSlug: input.publicSlug,
						publicConfig: input.publicConfig,
					},
				},
			),
		onSuccess: () => client.invalidateQueries({ queryKey: viewKeys.views(unref(entity)) }),
	});
}

// ---------------------------------------------------------------------------
// Writing structure from the SCREEN.
//
// All of this was agent-only, which made the builder a bottleneck for decisions
// a person makes in seconds — and left someone stuck whenever the model was
// unavailable. The gates are unchanged because they live on the server: the
// additive-versus-destructive check, identifier validation, and DDL as
// `bx_builder`. A screen cannot do more than an agent could.
// ---------------------------------------------------------------------------

export interface FieldInput {
	code: string;
	label: string;
	type: FieldType;
	required?: boolean;
	config?: Record<string, unknown>;
	helpText?: string | null;
}

export function useCreateEntity() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			code: string;
			name: string;
			description?: string | null;
			navGroup?: string | null;
			showInNav?: boolean;
			fields: FieldInput[];
		}) => request<EntityDefinition>("/v3/structure/entities", { method: "POST", body: input }),
		// A new entity changes the navigation, so the whole structure is stale.
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ["system"] });
			return client.invalidateQueries({ queryKey: structureKeys.entities() });
		},
	});
}

/** Pôr uma tela no menu ou tirá-la dele — o menu lateral lê a mesma lista de telas. */
export function useSetShowInNav() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { code: string; showInNav: boolean }) =>
			request(`/v3/structure/entities/${encodeURIComponent(input.code)}`, {
				method: "PATCH",
				body: { showInNav: input.showInNav },
			}),
		onSettled: () => {
			void client.invalidateQueries({ queryKey: ["system"] });
			return client.invalidateQueries({ queryKey: structureKeys.entities() });
		},
	});
}

/**
 * Quem pode — o `access` INTEIRO da tabela (o servidor substitui o objeto): o
 * papel mínimo segue como estava, e o escopo `own` com os donos explícitos.
 */
export function useSetEntityAccess() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { code: string; access: NonNullable<EntityDefinition["access"]> }) =>
			request(`/v3/structure/entities/${encodeURIComponent(input.code)}`, {
				method: "PATCH",
				body: { access: input.access },
			}),
		onSettled: () => {
			void client.invalidateQueries({ queryKey: ["system"] });
			return client.invalidateQueries({ queryKey: structureKeys.entities() });
		},
	});
}

/** A frase que diz o que a tabela de dados guarda; vazia = sem descrição. Entra no histórico. */
export function useSetEntityDescription() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { code: string; description: string }) =>
			request(`/v3/structure/entities/${encodeURIComponent(input.code)}`, {
				method: "PATCH",
				body: { description: input.description.trim() || null },
			}),
		onSettled: () => {
			void client.invalidateQueries({ queryKey: ["system"] });
			return client.invalidateQueries({ queryKey: structureKeys.entities() });
		},
	});
}

export function useAddField(entity: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: FieldInput & { confirmed?: boolean }) =>
			request<FieldDefinition>(`/v3/structure/entities/${unref(entity)}/fields`, {
				method: "POST",
				body: input,
			}),
		onSuccess: () => invalidateStructureAndRows(client, unref(entity)),
	});
}

/**
 * A column change is also a ROW change (19/09): a new formula recomputes every
 * record, an archived field leaves the cells, and the table was still showing
 * the rows it had fetched before. Structure and records go stale together.
 */
function invalidateStructureAndRows(client: ReturnType<typeof useQueryClient>, entity: string) {
	void client.invalidateQueries({ queryKey: ["system"] });
	void client.invalidateQueries({ queryKey: structureKeys.entities() });
	void client.invalidateQueries({ queryKey: ["records", entity] });
}

/**
 * Archiving, not dropping.
 *
 * The default hides the field from the UI and the API while the column and the
 * data remain — which is why the button can be ordinary rather than frightening.
 * A hard delete needs `hardDelete` AND `confirmed`, and the server asks twice.
 */
export function useArchiveField(entity: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: { fieldCode: string; hardDelete?: boolean; confirmed?: boolean }) =>
			request(`/v3/structure/entities/${unref(entity)}/fields/${input.fieldCode}`, {
				method: "DELETE",
				body: { hardDelete: input.hardDelete ?? false, confirmed: input.confirmed ?? false },
			}),
		onSuccess: () => invalidateStructureAndRows(client, unref(entity)),
	});
}

/**
 * The same two steps, for the whole screen.
 *
 * Default = archive: the entity leaves the navigation and the API while the
 * table and its rows stay, exactly as `useArchiveField` treats a column. A hard
 * delete needs `hardDelete` AND `confirmed`, and the server asks twice —
 * `NEEDS_CONFIRMATION` with `retryWith` comes back on the first ask. An entity
 * another entity points at is refused with `HAS_DEPENDENTS` and the codes of
 * who points, which the screen names rather than paraphrases.
 */
export function useArchiveEntity() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: { code: string; hardDelete?: boolean; confirmed?: boolean }) =>
			request<{ archived?: boolean; dropped?: boolean }>(`/v3/structure/entities/${input.code}`, {
				method: "DELETE",
				body: { hardDelete: input.hardDelete ?? false, confirmed: input.confirmed ?? false },
			}),
		// Everything under "structure": the entity list the navigation draws, the
		// entity's views, and the component screens the menu lists — a whole screen
		// just left, and every one of those answered about it.
		//
		// NOT awaited, on purpose. Awaiting it made `mutateAsync` resolve only after
		// the refetch: by then the entity page had re-rendered "no such screen",
		// unmounted the fields card, and the `archived` emit that moves the person
		// to /structure was lost — the page stayed on a dead address (E2E 09-03,
		// 05/09). The address moves first; the menu catches up on its own.
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ["structure"] });
			void client.invalidateQueries({ queryKey: ["system"] });
			// O funil sobre a tabela passa a dizer "parado" — e volta a andar no desarquivar.
			void client.invalidateQueries({ queryKey: ["board"] });
		},
	});
}

/**
 * Changing a field that already exists.
 *
 * Fields were create-only until this: a mistyped label, a select missing an option, a
 * field marked required by accident — all permanent, and the only way out was archiving
 * and adding a new one, which leaves the data behind in the old column.
 *
 * `code` is deliberately absent from the input. It IS the physical column name, and the
 * server rejects the key outright rather than ignoring it.
 *
 * Every field is optional and an omitted one is left alone, which is why `helpText` is
 * `string | null` — clearing it has to be sayable, and `undefined` means "don't touch".
 */
export function useEditField(entity: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			fieldCode: string;
			label?: string;
			type?: FieldType;
			required?: boolean;
			config?: Record<string, unknown>;
			helpText?: string | null;
			sortOrder?: number;
			showInList?: boolean;
			/** The person's yes to a formula replacing every typed value (19/09). */
			replaceValues?: boolean;
		}) => {
			const { fieldCode, ...body } = input;
			return request<FieldDefinition>(
				`/v3/structure/entities/${unref(entity)}/fields/${fieldCode}`,
				{ method: "PATCH", body },
			);
		},
		onSuccess: () => invalidateStructureAndRows(client, unref(entity)),
	});
}

/** Appearance only. Never emits DDL — that is the whole point of ADR-F3. */
export function useSetFieldDisplay(entity: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: { fieldCode: string; display: string }) =>
			request<FieldDefinition>(
				`/v3/structure/entities/${unref(entity)}/fields/${input.fieldCode}/display`,
				{ method: "PATCH", body: { display: input.display } },
			),
		onSuccess: () => client.invalidateQueries({ queryKey: structureKeys.entities() }),
	});
}

export function useAddRelation(entity: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			code: string;
			label: string;
			toEntity: string;
			kind: "one_to_many" | "many_to_one";
			required?: boolean;
		}) =>
			request<RelationDefinition>(`/v3/structure/entities/${unref(entity)}/relations`, {
				method: "POST",
				body: input,
			}),
		onSuccess: (_data, input) => {
			void client.invalidateQueries({ queryKey: ["structure"] });
			// The child gained a foreign-key column, so its definition changed too.
			void client.invalidateQueries({ queryKey: structureKeys.entities() });
			void client.invalidateQueries({ queryKey: viewKeys.relations(input.toEntity) });
		},
	});
}

export function useRenameRelation(entity: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { code: string; label: string }) =>
			request(`/v3/structure/entities/${unref(entity)}/relations/${input.code}`, {
				method: "PATCH",
				body: { label: input.label },
			}),
		onSuccess: () => client.invalidateQueries({ queryKey: ["structure"] }),
	});
}
